import {Transaction} from "../domain/Transaction";
import {Recurrence} from "../domain/Recurrence";
import {getCurrentMonth} from "../utils/getCurrentMonth";
import config from "../config";
import {FrequencyCode} from "../domain/Frequency";
import {v4 as uuidv4} from 'uuid'

export function generateRecurrenceTransactions(
  recurrence: Recurrence,
  existingTransactions: Transaction[],
  now: [number, number] = getCurrentMonth(),
): Transaction[] {
  switch (recurrence.frequency) {
    case FrequencyCode.MONTH:
      return generateMonthlyRecurringTransactions(recurrence, existingTransactions, now)
    case FrequencyCode.YEAR:
      return generateYearlyRecurringTransactions(recurrence, existingTransactions, now)
    default:
      throw (new Error(`Recurrence ${recurrence.id} has neither a month or year frequency`))
  }
}

const generateMonthlyRecurringTransactions = (recurrence: Recurrence, existingTransactions: Transaction[], now: [number, number]): Transaction[] => {
  const recurringTransactions: Transaction[] = []
  const [currentMonth, currentYear] = now
  const {appStartMonth, appStartYear, appEndMonth, appEndYear} = config
  const {
    id, type, amount, description,
    startMonth = currentMonth,
    startYear = currentYear,
    endMonth = appEndMonth,
    endYear = appEndYear,
  } = recurrence

  let [month, year] = clampToAppStart(startMonth, startYear, appStartMonth, appStartYear)

  while (isMonthBeforeEndOfPeriod(month, year, endMonth, endYear)) {
    if (!hasMaterializedOccurrence(existingTransactions, id, month, year)) {
      recurringTransactions.push(buildOccurrence(id, type, amount, description, month, year))
    }
    month++
    if (month > 12) { month = 1; year++ }
  }
  return recurringTransactions
}

const generateYearlyRecurringTransactions = (recurrence: Recurrence, existingTransactions: Transaction[], now: [number, number]): Transaction[] => {
  const recurringTransactions: Transaction[] = []
  const [currentMonth, currentYear] = now
  const {appStartMonth, appStartYear, appEndMonth, appEndYear} = config
  const {
    id, type, amount, description, onMonth,
    startMonth = currentMonth,
    startYear = currentYear,
    endMonth = appEndMonth,
    endYear = appEndYear,
  } = recurrence

  if (!onMonth) {
    throw (new Error(`Recurrence ${id} is set as yearly but has no onMonth property`))
  }

  const month = onMonth
  let year = startMonth > onMonth ? startYear + 1 : startYear
  // Lower-bound to the app start (month + year): an onMonth before appStartMonth
  // in the app-start year only becomes valid the following year.
  if (year < appStartYear || (year === appStartYear && month < appStartMonth)) {
    year = month < appStartMonth ? appStartYear + 1 : appStartYear
  }

  while (isMonthBeforeEndOfPeriod(month, year, endMonth, endYear)) {
    if (!hasMaterializedOccurrence(existingTransactions, id, month, year)) {
      recurringTransactions.push(buildOccurrence(id, type, amount, description, month, year))
    }
    year++
  }
  return recurringTransactions
}

const buildOccurrence = (id: string, type: Recurrence['type'], amount: number, description: string, month: number, year: number): Transaction => ({
  id: uuidv4(),
  year,
  month,
  description,
  amount,
  type,
  recurrenceId: id,
  checked: false,
})

// Only a MATERIALIZED occurrence (edited: checked or amount-modified) blocks regeneration.
// A freshly-generated unchecked occurrence already present in the input must be regenerated,
// otherwise the recurrence-change effect in LoanTracker (which passes the current transactions
// as `existing`) would drop every unchecked occurrence.
const hasMaterializedOccurrence = (existing: Transaction[], recurrenceId: string, month: number, year: number): boolean =>
  existing.some(t =>
    t.recurrenceId === recurrenceId && t.year === year && t.month === month &&
    (t.checked || (t.amountModifications?.length ?? 0) > 0),
  )

const clampToAppStart = (month: number, year: number, appStartMonth: number, appStartYear: number): [number, number] => {
  if (year < appStartYear || (year === appStartYear && month < appStartMonth)) {
    return [appStartMonth, appStartYear]
  }
  return [month, year]
}

const isMonthBeforeEndOfPeriod = (month: number, year: number, endMonth: number, endYear: number) => {
  return year < endYear || (year === endYear && month <= endMonth)
}
